import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { useLanguage } from "../contexts/LanguageContext";
import Footer from "../components/Footer";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Handshake,
  DollarSign,
  Users,
  Percent,
  TrendingUp,
  Facebook,
  Store,
  CheckCircle,
  Gift,
  Shield,
  Clock,
  ArrowRight,
  Loader2,
  Mail,
  Phone,
  Building2,
  Link as LinkIcon,
  Send,
  Sparkles,
  Home,
  LogOut,
} from "lucide-react";

const BecomePartnerPage = ({ user, onLogout, refreshUserData }) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    partner_type: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    facebook_group_name: "",
    facebook_group_url: "",
    group_member_count: "",
    company_name: "",
    website: "",
    message: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.partner_type || !formData.email || !formData.first_name || !formData.last_name) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    if (formData.partner_type === "admin_groupe" && !formData.facebook_group_url) {
      toast.error("Veuillez fournir l'URL de votre groupe Facebook");
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${API}/partners/apply`, formData);
      setSubmitted(true);
      toast.success("Candidature envoyée avec succès!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi de la candidature");
    } finally {
      setSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: Percent,
      title: "25% de commission",
      description: "Sur le premier transfert de chaque membre référé",
      color: "text-green-600",
      bgColor: "bg-green-100",
    },
    {
      icon: TrendingUp,
      title: "5% récurrent",
      description: "Sur tous les transferts suivants de vos membres",
      color: "text-blue-600",
      bgColor: "bg-blue-100",
    },
    {
      icon: Gift,
      title: "25% de réduction",
      description: "Offerte à vos membres sur leur premier transfert",
      color: "text-purple-600",
      bgColor: "bg-purple-100",
    },
    {
      icon: Shield,
      title: "Crédibilité",
      description: "Recommandez un service sécurisé et fiable à vos membres",
      color: "text-brand-600",
      bgColor: "bg-brand-100",
    },
  ];

  const steps = [
    {
      number: "1",
      title: "Inscrivez-vous",
      description: "Remplissez le formulaire de candidature avec les informations de votre groupe ou entreprise",
    },
    {
      number: "2",
      title: "Recevez votre code",
      description: "Une fois approuvé, vous recevrez votre code promo unique personnalisé",
    },
    {
      number: "3",
      title: "Partagez & Gagnez",
      description: "Partagez votre code avec vos membres et gagnez des commissions sur chaque transfert",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
              <img src="/logo-dark-cropped.png" alt="Middleman" className="h-5 md:h-6 w-auto" />
            </Link>
            
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              {user ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/dashboard')}
                  >
                    <Home className="h-4 w-4 sm:mr-2" />
                    <span className="hidden sm:inline">{language === 'fr' ? 'Mon compte' : 'Dashboard'}</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onLogout}
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  onClick={() => navigate('/')}
                  className="bg-brand-600 hover:bg-brand-700"
                >
                  {language === 'fr' ? 'Connexion' : 'Login'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-brand-600 to-purple-600 opacity-5"></div>
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-brand-100 text-brand-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Sparkles className="h-4 w-4" />
              Programme Partenaire
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Devenez Partenaire
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-600 to-purple-600"> Middleman</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Vous gérez un groupe Facebook de revente de billets ou vous êtes revendeur? 
              Gagnez des commissions en recommandant Middleman à vos membres!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                onClick={() => setShowApplicationForm(true)}
                className="bg-gradient-to-r from-brand-600 to-purple-600 hover:from-brand-700 hover:to-purple-700 text-white px-8"
                data-testid="become-partner-cta"
              >
                <Handshake className="h-5 w-5 mr-2" />
                Devenir Partenaire
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
              >
                En savoir plus
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Pourquoi devenir partenaire?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Rejoignez notre programme et bénéficiez d'avantages exclusifs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className={`${benefit.bgColor} w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                    <benefit.icon className={`h-8 w-8 ${benefit.color}`} />
                  </div>
                  <h3 className="font-bold text-xl mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section id="how-it-works" className="py-16 bg-gradient-to-br from-gray-50 to-brand-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comment ça fonctionne?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Un processus simple en 3 étapes
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              {steps.map((step, index) => (
                <div key={index} className="relative">
                  <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow h-full">
                    <div className="w-12 h-12 bg-gradient-to-r from-brand-600 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold text-xl mb-4">
                      {step.number}
                    </div>
                    <h3 className="font-bold text-lg mb-2">{step.title}</h3>
                    <p className="text-gray-600 text-sm">{step.description}</p>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                      <ArrowRight className="h-6 w-6 text-brand-300" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Partner Types Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Qui peut devenir partenaire?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="border-2 border-blue-200 hover:border-blue-400 transition-colors">
              <CardHeader>
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                  <Facebook className="h-7 w-7 text-blue-600" />
                </div>
                <CardTitle className="text-xl">Admins de Groupes Facebook</CardTitle>
                <CardDescription>
                  Vous gérez un groupe de revente de billets sur Facebook?
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Groupes de revente de billets (sports, spectacles, concerts)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Code promo personnalisé au nom de votre groupe</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Tableau de bord pour suivre vos commissions</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-purple-200 hover:border-purple-400 transition-colors">
              <CardHeader>
                <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                  <Store className="h-7 w-7 text-purple-600" />
                </div>
                <CardTitle className="text-xl">Revendeurs & Entreprises</CardTitle>
                <CardDescription>
                  Vous êtes revendeur de billets ou avez une entreprise liée aux événements?
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Revendeurs professionnels de billets</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Agences de voyages et organisateurs d'événements</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Taux de commission négociables selon le volume</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Prêt à rejoindre le programme?
          </h2>
          <p className="text-gray-600 mb-8 max-w-xl mx-auto">
            Remplissez le formulaire ci-dessous et notre équipe vous contactera dans les 24-48 heures.
          </p>
          <Button 
            size="lg" 
            onClick={() => setShowApplicationForm(true)}
            className="bg-gradient-to-r from-brand-600 to-purple-600 hover:from-brand-700 hover:to-purple-700 text-white px-8"
          >
            <Send className="h-5 w-5 mr-2" />
            Soumettre ma candidature
          </Button>
        </div>
      </section>

      <Footer />

      {/* Application Form Dialog */}
      <Dialog open={showApplicationForm} onOpenChange={setShowApplicationForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <DialogTitle className="text-2xl mb-2">Candidature envoyée!</DialogTitle>
              <DialogDescription className="mb-6">
                Merci pour votre intérêt! Notre équipe examinera votre candidature et vous contactera 
                dans les 24-48 heures par courriel.
              </DialogDescription>
              <Button onClick={() => {
                setShowApplicationForm(false);
                setSubmitted(false);
                setFormData({
                  partner_type: "",
                  first_name: "",
                  last_name: "",
                  email: "",
                  phone: "",
                  facebook_group_name: "",
                  facebook_group_url: "",
                  group_member_count: "",
                  company_name: "",
                  website: "",
                  message: "",
                });
              }}>
                Fermer
              </Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Handshake className="h-5 w-5 text-brand-600" />
                  Devenir Partenaire Middleman
                </DialogTitle>
                <DialogDescription>
                  Remplissez le formulaire ci-dessous. Les champs avec * sont obligatoires.
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Partner Type */}
                <div className="space-y-2">
                  <Label>Type de partenariat *</Label>
                  <Select
                    value={formData.partner_type}
                    onValueChange={(v) => setFormData(f => ({ ...f, partner_type: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionnez un type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin_groupe">
                        <div className="flex items-center gap-2">
                          <Facebook className="h-4 w-4 text-blue-600" />
                          Admin de Groupe Facebook
                        </div>
                      </SelectItem>
                      <SelectItem value="revendeur">
                        <div className="flex items-center gap-2">
                          <Store className="h-4 w-4 text-purple-600" />
                          Revendeur / Entreprise
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Personal Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Prénom *</Label>
                    <Input
                      placeholder="Jean"
                      value={formData.first_name}
                      onChange={(e) => setFormData(f => ({ ...f, first_name: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nom *</Label>
                    <Input
                      placeholder="Tremblay"
                      value={formData.last_name}
                      onChange={(e) => setFormData(f => ({ ...f, last_name: e.target.value }))}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Courriel *
                  </Label>
                  <Input
                    type="email"
                    placeholder="jean@exemple.com"
                    value={formData.email}
                    onChange={(e) => setFormData(f => ({ ...f, email: e.target.value }))}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Téléphone
                  </Label>
                  <Input
                    type="tel"
                    placeholder="514-555-1234"
                    value={formData.phone}
                    onChange={(e) => setFormData(f => ({ ...f, phone: e.target.value }))}
                  />
                </div>

                {/* Facebook Group Fields */}
                {formData.partner_type === "admin_groupe" && (
                  <>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Facebook className="h-4 w-4 text-blue-600" />
                        Nom du groupe Facebook *
                      </Label>
                      <Input
                        placeholder="Fans des Canadiens - Revente Billets"
                        value={formData.facebook_group_name}
                        onChange={(e) => setFormData(f => ({ ...f, facebook_group_name: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <LinkIcon className="h-4 w-4" />
                        URL du groupe *
                      </Label>
                      <Input
                        placeholder="https://facebook.com/groups/..."
                        value={formData.facebook_group_url}
                        onChange={(e) => setFormData(f => ({ ...f, facebook_group_url: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Nombre de membres (approximatif)
                      </Label>
                      <Input
                        type="number"
                        placeholder="5000"
                        value={formData.group_member_count}
                        onChange={(e) => setFormData(f => ({ ...f, group_member_count: e.target.value }))}
                      />
                    </div>
                  </>
                )}

                {/* Company Fields */}
                {formData.partner_type === "revendeur" && (
                  <>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        Nom de l'entreprise
                      </Label>
                      <Input
                        placeholder="Billets Express Inc."
                        value={formData.company_name}
                        onChange={(e) => setFormData(f => ({ ...f, company_name: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <LinkIcon className="h-4 w-4" />
                        Site web
                      </Label>
                      <Input
                        placeholder="https://www.monentreprise.com"
                        value={formData.website}
                        onChange={(e) => setFormData(f => ({ ...f, website: e.target.value }))}
                      />
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <Label>Message (optionnel)</Label>
                  <Textarea
                    placeholder="Décrivez brièvement votre activité ou pourquoi vous souhaitez devenir partenaire..."
                    value={formData.message}
                    onChange={(e) => setFormData(f => ({ ...f, message: e.target.value }))}
                    rows={3}
                  />
                </div>

                <div className="pt-4 flex gap-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowApplicationForm(false)}
                    className="flex-1"
                  >
                    Annuler
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={submitting}
                    className="flex-1 bg-gradient-to-r from-brand-600 to-purple-600 hover:from-brand-700 hover:to-purple-700"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Envoi...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Envoyer
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BecomePartnerPage;
